var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
export var initialEditorState = {
    config: { tabs: [] },
    optimisticLock: null,
    isDirty: false,
    lastReseededAt: null,
};
// ─── helpers ────────────────────────────────────────────────────────
var moveItem = function (items, from, to) {
    if (from === to)
        return items.slice();
    if (from < 0 || from >= items.length || to < 0 || to >= items.length) {
        return items.slice();
    }
    var next = items.slice();
    var picked = next.splice(from, 1)[0];
    next.splice(to, 0, picked);
    return next;
};
var updateAt = function (items, idx, updater) {
    if (idx < 0 || idx >= items.length)
        return items.slice();
    return items.map(function (item, i) { return (i === idx ? updater(item) : item); });
};
var removeAt = function (items, idx) {
    if (idx < 0 || idx >= items.length)
        return items.slice();
    return items.filter(function (_, i) { return i !== idx; });
};
var updateTab = function (state, tabIdx, updater) { return (__assign(__assign({}, state), { isDirty: true, config: __assign(__assign({}, state.config), { tabs: updateAt(state.config.tabs, tabIdx, updater) }) })); };
var updateField = function (state, tabIdx, fieldIdx, updater) {
    return updateTab(state, tabIdx, function (tab) { return (__assign(__assign({}, tab), { fields: updateAt(tab.fields, fieldIdx, updater) })); });
};
/**
 * CZNA-500 — a raw template may carry a field with NO `subfields` key at all.
 * MARC control fields are stored that way (`{"tag":"001",…}`), because they
 * have no subfields; only the EFC compiler fills in `subfields: []`. Every
 * subfield action therefore has to read through this instead of touching
 * `field.subfields` directly, or a stray dispatch spreads `undefined`.
 */
var subfieldsOf = function (field) { var _a; return (_a = field.subfields) !== null && _a !== void 0 ? _a : []; };
var updateSubfield = function (state, tabIdx, fieldIdx, subfieldIdx, updater) {
    return updateField(state, tabIdx, fieldIdx, function (field) { return (__assign(__assign({}, field), { subfields: updateAt(subfieldsOf(field), subfieldIdx, updater) })); });
};
// ─── reducer ─────────────────────────────────────────────────────────
export var editorReducer = function (state, action) {
    switch (action.type) {
        case 'reseed':
            return {
                config: action.payload.value,
                optimisticLock: action.payload.optimisticLock,
                isDirty: false,
                lastReseededAt: action.payload.updatedAt,
            };
        case 'seedFromOverride':
            // Replace config with the override but preserve the existing
            // optimisticLock and lastReseededAt — the override carries user
            // edits that haven't been saved yet, so the editor must be dirty.
            return __assign(__assign({}, state), { config: action.value, isDirty: true });
        case 'moveTab':
            if (action.from === action.to)
                return state;
            return __assign(__assign({}, state), { isDirty: true, config: __assign(__assign({}, state.config), { tabs: moveItem(state.config.tabs, action.from, action.to) }) });
        case 'updateTabLabel':
            return updateTab(state, action.tabIdx, function (tab) { return (__assign(__assign({}, tab), { label: action.label })); });
        case 'addTab':
            return __assign(__assign({}, state), { isDirty: true, config: __assign(__assign({}, state.config), { tabs: __spreadArray(__spreadArray([], state.config.tabs, true), [action.tab], false) }) });
        case 'removeTab':
            return __assign(__assign({}, state), { isDirty: true, config: __assign(__assign({}, state.config), { tabs: removeAt(state.config.tabs, action.tabIdx) }) });
        case 'moveField':
            return updateTab(state, action.tabIdx, function (tab) { return (__assign(__assign({}, tab), { fields: moveItem(tab.fields, action.from, action.to) })); });
        case 'moveFieldToTab': {
            var fromTabIdx_1 = action.fromTabIdx, fieldIdx_1 = action.fieldIdx, toTabIdx_1 = action.toTabIdx;
            if (fromTabIdx_1 === toTabIdx_1)
                return state;
            var source = state.config.tabs[fromTabIdx_1];
            var target = state.config.tabs[toTabIdx_1];
            // Out-of-range indices are a no-op rather than a crash: the panel derives
            // them from render-time state, and a reseed between render and click can
            // legitimately invalidate them.
            if (!source || !target)
                return state;
            var field_1 = source.fields[fieldIdx_1];
            if (!field_1)
                return state;
            return __assign(__assign({}, state), { isDirty: true, config: __assign(__assign({}, state.config), { tabs: state.config.tabs.map(function (tab, i) {
                        if (i === fromTabIdx_1)
                            return __assign(__assign({}, tab), { fields: removeAt(tab.fields, fieldIdx_1) });
                        // Appended, not inserted at a position: the target tab is not on
                        // screen when the move is triggered, so there is no position the
                        // admin could have meant. Reordering inside the target is the
                        // existing drag-and-drop.
                        if (i === toTabIdx_1)
                            return __assign(__assign({}, tab), { fields: __spreadArray(__spreadArray([], tab.fields, true), [field_1], false) });
                        return tab;
                    }) }) });
        }
        case 'addField':
            return updateTab(state, action.tabIdx, function (tab) { return (__assign(__assign({}, tab), { fields: __spreadArray(__spreadArray([], tab.fields, true), [action.field], false) })); });
        case 'removeField':
            return updateTab(state, action.tabIdx, function (tab) { return (__assign(__assign({}, tab), { fields: removeAt(tab.fields, action.fieldIdx) })); });
        case 'updateFieldProperty':
            return updateField(state, action.tabIdx, action.fieldIdx, function (field) {
                var next = __assign({}, field);
                if (action.value === undefined)
                    delete next[action.key];
                else
                    next[action.key] = action.value;
                return next;
            });
        case 'moveSubfield':
            return updateField(state, action.tabIdx, action.fieldIdx, function (field) { return (__assign(__assign({}, field), { subfields: moveItem(subfieldsOf(field), action.from, action.to) })); });
        case 'addSubfield':
            return updateField(state, action.tabIdx, action.fieldIdx, function (field) {
                var _a;
                var next = __assign(__assign({}, field), { subfields: __spreadArray(__spreadArray([], subfieldsOf(field), true), [action.subfield], false) });
                // CZNA-1527 — a declared code cannot also be kept out of "+ Přidat podpole…"
                // (FormConfigValidator refuses it), so declaring it settles the exclusion.
                if ((_a = next.offerExcludedSubfields) === null || _a === void 0 ? void 0 : _a.includes(action.subfield.code)) {
                    var rest = next.offerExcludedSubfields.filter(function (code) { return code !== action.subfield.code; });
                    if (rest.length)
                        next.offerExcludedSubfields = rest;
                    else
                        delete next.offerExcludedSubfields;
                }
                return next;
            });
        case 'removeSubfield':
            return updateField(state, action.tabIdx, action.fieldIdx, function (field) { return (__assign(__assign({}, field), { subfields: removeAt(subfieldsOf(field), action.subfieldIdx) })); });
        case 'updateSubfieldProperty':
            return updateSubfield(state, action.tabIdx, action.fieldIdx, action.subfieldIdx, function (sf) {
                // `undefined` deletes the key — the subfield twin of
                // `updateFieldProperty` (CZNA-544/566). For `label` an absent key
                // inherits the ontology's name, a present-but-empty one does not.
                var next = __assign({}, sf);
                if (action.value === undefined)
                    delete next[action.key];
                else
                    next[action.key] = action.value;
                return next;
            });
        case 'updateSubfieldProperties':
            return updateSubfield(state, action.tabIdx, action.fieldIdx, action.subfieldIdx, function (sf) {
                // Apply each patch entry. `undefined` deletes the key entirely
                // instead of carrying it as a JSON null/undefined sentinel.
                var next = __assign({}, sf);
                for (var _i = 0, _a = Object.entries(action.patch); _i < _a.length; _i++) {
                    var _b = _a[_i], k = _b[0], v = _b[1];
                    if (v === undefined)
                        delete next[k];
                    else
                        next[k] = v;
                }
                return next;
            });
        case 'updateIndicator':
            return updateField(state, action.tabIdx, action.fieldIdx, function (field) { return (__assign(__assign({}, field), (action.which === 1
                ? { indicator1Def: action.indicator }
                : { indicator2Def: action.indicator }))); });
        case 'updateComponentProperty':
            return updateSubfield(state, action.tabIdx, action.fieldIdx, action.subfieldIdx, function (sf) {
                if (!sf.components)
                    return sf;
                return __assign(__assign({}, sf), { components: updateAt(sf.components, action.componentIdx, function (comp) {
                        var _a;
                        return (__assign(__assign({}, comp), (_a = {}, _a[action.key] = action.value, _a)));
                    }) });
            });
        default: {
            // Exhaustiveness assertion — if a new action type is added without a
            // case branch, TypeScript will narrow `action` to `never` and the
            // assignment below will fail compile. The runtime `return state`
            // makes the reducer total even if the union grows.
            var _exhaustive = action;
            // Reference _exhaustive to keep the exhaustiveness check meaningful
            if (_exhaustive)
                return state;
            return state;
        }
    }
};
